// An error as the aide view shows it: the child-facing outcome a reader
// would get, and underneath it the whole error value — every field, since
// this page is where the kinds are read, not hidden.
import {
  type CaptureError,
  childFacing,
  type TranscriptionError,
} from "@bindery/speech/contract";
import { TriangleAlert } from "lucide-react";

type AnyError = CaptureError | TranscriptionError;

// What an operator of the lab, rather than a child, should do next — for
// the kinds whose cause is this deployment or this browser rather than the
// recording.
const HINTS: Partial<Record<AnyError["kind"], string>> = {
  unauthenticated:
    "Signed in but refused? Your broker subject is not on SPEECH_LAB_ALLOWED_SUBJECTS — the speech_lab.auth.refused log event carries it.",
  "provider-unavailable":
    "Status 503 from the lab itself means the Worker's speech config is invalid — see speech_lab.config.invalid in the logs.",
  "attempts-exhausted":
    "The lab caps attempts per item at 3. Start a new item to reset the count.",
  "permission-denied":
    "Re-enable this site's microphone permission in the browser, then record again. Note in the device report whether the prompt came back.",
  unsupported:
    "This browser cannot record here. Record the reason in the device report; microphone capture also needs HTTPS (localhost counts).",
};

export function ErrorReadout({ error }: { error: AnyError }) {
  const hint = HINTS[error.kind];
  return (
    <div className="border-destructive/40 bg-destructive/5 rounded-sm border">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b border-inherit px-3 py-2">
        <TriangleAlert className="text-destructive size-4 self-center" />
        <span className="lab-label">child sees</span>
        <span className="lab-readout text-destructive text-base font-medium">
          {childFacing(error)}
        </span>
        <span className="lab-label ml-auto">kind</span>
        <span className="lab-readout font-medium">{error.kind}</span>
      </div>
      <pre className="lab-readout overflow-x-auto px-3 py-2 text-xs">
        {JSON.stringify(error, null, 2)}
      </pre>
      {hint === undefined ? null : (
        <p className="text-muted-foreground border-t border-inherit px-3 py-2 text-xs">
          {hint}
        </p>
      )}
    </div>
  );
}
